/**
 * Dynamic-generation status UI (M12 + FR-9.9) — the transient-status component
 * rendered by every role while a game is `GENERATING`.
 *
 * It renders the authoritative `GameState.generation` block (which the server keeps
 * in sync with the streamed `generation_status` events), so a client that connects
 * mid-generation shows the same live status.
 *
 * FR-9.9 (issue #31) turns the single-stage view into legible progress feedback:
 *  - a fixed, ordered **step list** of the six pipeline stages, each shown as
 *    done / active / pending (derived purely from the current stage's ordinal — no
 *    new wire data). The active step carries the streamed `message`, and during
 *    `WRITING_CLUES` the determinate `N of M` bar the agent reports;
 *  - a live **elapsed** counter with a soft (non-binding) sense of expected
 *    duration — never a hard countdown, since real LLM latency varies widely (the
 *    server timeout is the only authority on giving up).
 *
 * On `generation_failed`, {@link GenerationFailedBanner} shows a back-to-lobby
 * terminal state to EVERY role (the server has already returned the game to LOBBY);
 * only the host is offered the recovery action (FR-9.5 one-goal-one-action).
 */

import { useEffect, useRef, useState } from 'react';
import type {
  GenerationFailureCode,
  GenerationStage,
  GenerationState,
} from '@jeopardy/shared';

/**
 * The pipeline stages in the fixed order the generator emits them (M12 §4.2). The
 * ordinal position defines the done/active/pending partition — this is a
 * display-only map, not on the wire.
 */
const GENERATION_STAGES: readonly { stage: GenerationStage; label: string }[] = [
  { stage: 'SELECTING_CATEGORIES', label: 'Balancing categories' },
  { stage: 'WRITING_CLUES', label: 'Writing clues' },
  { stage: 'CHECKING_COHERENCY', label: 'Checking questions & answers' },
  { stage: 'DEDUPING', label: 'Checking for repeats' },
  { stage: 'VALIDATING', label: 'Validating the board' },
  { stage: 'FINALIZING', label: 'Finalizing' },
];

/**
 * Past this elapsed time we swap the "usually takes ~X" reassurance for a
 * "still working" one. Comfortably above the ~100s typical opus-4.8 run and below
 * the 240s server budget ([[m12-structure]]). Display-only — it gates nothing.
 */
const SLOW_THRESHOLD_MS = 120_000;

/** Format an elapsed millisecond span as `m:ss` (clamped at zero). */
export function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** The soft, non-binding duration hint shown under the elapsed counter. */
export function estimateHint(elapsedMs: number): string {
  return elapsedMs < SLOW_THRESHOLD_MS
    ? 'This usually takes about a minute and a half.'
    : 'Still working — a complex board can take a little longer. Hang tight.';
}

/**
 * Live elapsed time since the generation started, ticking ~once a second.
 *
 * Anchors to the server-authoritative `startedAtEpochMs` when present (so every
 * client — including one that joined mid-generation — agrees), falling back to the
 * client's own first-observed time when the stream-only fold left it `0`
 * (game-store.ts). Clamped at zero so client/server clock skew never shows a
 * negative elapsed.
 */
function useElapsedMs(startedAtEpochMs: number): number {
  const firstSeenRef = useRef<number>(Date.now());
  const anchor = startedAtEpochMs > 0 ? startedAtEpochMs : firstSeenRef.current;
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return Math.max(0, now - anchor);
}

interface GenerationStatusProps {
  generation: GenerationState;
}

/**
 * Generic loading state (#346, D346.6/D346.7). Shown until a live LLM run is actually
 * committed — a board assembled entirely from the ClueStore (demo / store-covered games)
 * and the pre-generator "deciding" window are NOT live, so the detailed six-step pipeline
 * (and its "~a minute and a half" promise) would be misleading. A spinner + one neutral
 * line, reusing the status-panel shell; no step list, no elapsed counter, no estimate.
 */
export function GenerationLoading(): React.JSX.Element {
  return (
    <div className="generation-loading" data-testid="generation-loading">
      <span className="generation-loading__spinner" aria-hidden="true" />
      <span className="generation-loading__label" aria-live="polite">
        Building your board…
      </span>
    </div>
  );
}

export function GenerationStatus({
  generation,
}: GenerationStatusProps): React.JSX.Element {
  const { stage, message, progress, startedAtEpochMs, live } = generation;
  const elapsedMs = useElapsedMs(startedAtEpochMs);

  // Only a committed live run gets the detailed pipeline (#346). Undecided / store-hit /
  // demo generation (`live !== true`) shows the generic loader instead.
  if (live !== true) {
    return <GenerationLoading />;
  }

  const currentIndex = Math.max(
    0,
    GENERATION_STAGES.findIndex((s) => s.stage === stage),
  );
  // A determinate bar only when the agent reports progress (during WRITING_CLUES);
  // total 0 would be meaningless, so guard it.
  const showBar = progress !== undefined && progress.total > 0;
  const pct = showBar
    ? Math.min(100, Math.round((progress.done / progress.total) * 100))
    : 0;

  return (
    <div className="generation-status" data-testid="generation-status">
      <ol className="generation-steps" data-testid="generation-steps">
        {GENERATION_STAGES.map(({ stage: s, label }, i) => {
          const stepState =
            i < currentIndex ? 'done' : i === currentIndex ? 'active' : 'pending';
          const active = stepState === 'active';
          return (
            <li
              key={s}
              className={`generation-step generation-step--${stepState}`}
              data-testid={`generation-step-${s}`}
              data-state={stepState}
              aria-current={active ? 'step' : undefined}
            >
              <span className="generation-step__marker" aria-hidden="true">
                {stepState === 'done' ? '✓' : active ? '›' : '•'}
              </span>
              <span
                className="generation-step__label"
                data-testid={active ? 'generation-stage' : undefined}
              >
                {label}
              </span>
              {/* The active step shows a detail body only when it adds information
                  beyond the label — i.e. the determinate WRITING_CLUES step, whose
                  message names the category being written. For the other stages the
                  streamed message is just the label + "…" (e.g. "Checking for
                  repeats" / "Checking for repeats…"), so we drop the redundant body
                  and let the emphasized step label carry it. */}
              {active && showBar && (
                <div className="generation-step__detail">
                  <div
                    className="generation-status__message"
                    data-testid="generation-message"
                    aria-live="polite"
                  >
                    {message}
                  </div>
                  <div
                    className="generation-status__bar"
                    role="progressbar"
                    data-testid="generation-progress"
                    aria-valuemin={0}
                    aria-valuemax={progress.total}
                    aria-valuenow={progress.done}
                  >
                    <div
                      className="generation-status__bar-fill"
                      style={{ width: `${pct}%` }}
                    />
                    <span className="generation-status__bar-label">
                      {progress.done} of {progress.total}
                    </span>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <div className="generation-status__timing">
        <span
          className="generation-status__elapsed"
          data-testid="generation-elapsed"
          aria-live="off"
        >
          {formatElapsed(elapsedMs)}
        </span>
        <span className="generation-status__estimate" data-testid="generation-estimate">
          {estimateHint(elapsedMs)}
        </span>
      </div>
    </div>
  );
}

/**
 * Human, non-technical reason per failure code (FR-9.9). Shown to EVERY role; the
 * server-provided `message` is rendered as secondary detail below it.
 */
const FAILURE_REASONS: Record<GenerationFailureCode, string> = {
  CANCELLED: 'Generation was cancelled.',
  TIMEOUT: 'Generation took too long and timed out.',
  GENERATOR_ERROR: 'The question generator ran into a problem.',
  INVALID_RESULT: 'The generated board didn’t pass validation.',
};

interface GenerationFailedBannerProps {
  /** Failure code from the server; drives the human-readable reason. */
  code: GenerationFailureCode;
  /** Server-provided detail message, shown as secondary text. */
  message: string;
  /**
   * Host-only recovery affordance (FR-9.5). Omitted for players/presentation, who
   * then see a read-only "waiting for the host" line instead.
   */
  onRetry?: () => void;
}

export function GenerationFailedBanner({
  code,
  message,
  onRetry,
}: GenerationFailedBannerProps): React.JSX.Element {
  return (
    <div className="error-text generation-failed" data-testid="generation-failed">
      <p className="generation-failed__reason">
        {FAILURE_REASONS[code]} Everyone is back in the lobby.
      </p>
      {message && (
        <p className="generation-failed__detail" data-testid="generation-failed-detail">
          {message}
        </p>
      )}
      {onRetry ? (
        <button
          type="button"
          className="btn btn--primary"
          data-testid="generation-retry"
          onClick={onRetry}
        >
          Try generating again
        </button>
      ) : (
        <p className="muted" data-testid="generation-failed-waiting">
          Waiting for the host to try again…
        </p>
      )}
    </div>
  );
}
